import { PERMISSIONS } from '@ibe/constants';
import { Button, ConfirmDialog, StatusBadge, toneForStatus } from '@ibe/ui';
import { Eye, Pencil, Plus, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router-dom';

import { PermissionGuard } from '../../../auth/permission-guard.js';
import { RowActions } from '../../../components/resource-list/cells.js';
import { ResourceListPage } from '../../../components/resource-list/resource-list-page.js';
import { type ColumnDescriptor } from '../../../lib/resource-list/columns.js';
import { useSoftDeleteUser } from '../api/mutations.js';
import { userEndpoints } from '../api/endpoints.js';
import { userRow, type UserRow } from '../api/schemas.js';

/**
 * The users list.
 *
 * Search, sort and pagination come from the shared list page; this file is
 * only the descriptor that says what each column means and where the New button
 * goes.
 */

function UserRowActions({ row }: { readonly row: UserRow }): React.JSX.Element {
  const [confirming, setConfirming] = useState(false);
  const softDelete = useSoftDeleteUser();

  return (
    <>
      <div className="flex flex-nowrap items-center justify-end gap-1">
        <RowActions
          actions={[
            { label: `View ${row.displayName}`, icon: Eye, to: `/users/${row.id}` },
            { label: `Edit ${row.displayName}`, icon: Pencil, to: `/users/${row.id}/edit` },
          ]}
        />
        <PermissionGuard permission={PERMISSIONS.USER_WRITE}>
          <RowActions
            actions={[
              {
                label: `Delete ${row.displayName}`,
                icon: Trash2,
                destructive: true,
                onClick: () => {
                  setConfirming(true);
                },
              },
            ]}
          />
        </PermissionGuard>
      </div>
      <ConfirmDialog
        open={confirming}
        onOpenChange={setConfirming}
        title="Delete user"
        description={`${row.displayName} will be removed from the user list. Their audit trail and historical actions are retained.`}
        confirmLabel="Delete"
        destructive
        loading={softDelete.isPending}
        onConfirm={() => {
          softDelete.mutate(row.id, {
            onSuccess: () => {
              setConfirming(false);
            },
          });
        }}
      />
    </>
  );
}

const columns: readonly ColumnDescriptor<UserRow>[] = [
  {
    id: 'displayName',
    header: 'Name',
    sortable: true,
    alwaysVisible: true,
    fields: ['displayName'],
    cell: (row) => (
      <Link className="font-medium underline-offset-4 hover:underline" to={`/users/${row.id}`}>
        {row.displayName}
      </Link>
    ),
  },
  {
    id: 'email',
    header: 'Email',
    sortable: true,
    fields: ['email'],
    cell: (row) => row.email,
  },
  {
    id: 'roles',
    header: 'Roles',
    fields: ['roles'],
    cell: (row) => row.roles.join(', '),
  },
  {
    id: 'status',
    header: 'Status',
    fields: ['status'],
    cell: (row) => <StatusBadge tone={toneForStatus(row.status)}>{row.status}</StatusBadge>,
  },
  {
    id: 'actions',
    header: '',
    headerLabel: 'Actions',
    align: 'right',
    alwaysVisible: true,
    fields: ['displayName'],
    cell: (row) => <UserRowActions row={row} />,
  },
];

export function UserListPage(): React.JSX.Element {
  return (
    <ResourceListPage
      title="Users"
      description="Staff accounts and the roles they hold."
      path={userEndpoints.list}
      resource="users"
      schema={userRow}
      columns={columns}
      rowKey={(row) => row.id}
      permission={PERMISSIONS.USER_READ}
      defaultSort={{ columnId: 'displayName', direction: 'asc' }}
      actions={
        <PermissionGuard permission={PERMISSIONS.USER_WRITE}>
          <Button asChild>
            <Link to="/users/new">
              <Plus className="h-4 w-4" aria-hidden="true" />
              New user
            </Link>
          </Button>
        </PermissionGuard>
      }
    />
  );
}
